import "./src/styles/globals.css"
import { LanguageProvider } from "./src/context/LanguageContext"
const React = require("react")

// Voiceflow/chatbot consent + timing helpers
// Additional gating ensures load only after the intro scroll animation completes.
const CHATBOT_LOAD_DELAY = 0 // extra delay before loading Voiceflow after consent + scroll
// How long the intro takes end to end: the loader timeline runs ~4.7s, then the
// landing page waits 0.1s and auto-scrolls to the attitude section for 2.5s.
// Only when that scroll finishes is the visitor actually on the attitude
// section — that is the moment the chatbot may appear, and the authoritative
// signal for it is the "attitudeScrollComplete" event.
const CHATBOT_INTRO_DURATION = 7500
// Safety net for the rare case where the completion event never arrives (the
// animation was interrupted, the #attitude target is missing, ...). Counted in
// FOREGROUND time only: a background tab freezes the intro instead of finishing
// it, so a wall-clock timer would open the gate while the intro is still
// pending. Must stay comfortably above CHATBOT_INTRO_DURATION.
const CHATBOT_SCROLL_GATE_FALLBACK = CHATBOT_INTRO_DURATION + 4000
// The chatbot itself no longer waits for consent, but the chat-history
// persistence mode is fixed at load time. Loading while the banner is still
// unanswered would lock the session into in-memory history even if the visitor
// then accepts — and the widget is hidden behind the banner anyway. So give the
// answer a short window to arrive; after that, load regardless.
const CHATBOT_CONSENT_ANSWER_WAIT = 15000
// Retry network loads of the Voiceflow bundle so a single CDN hiccup doesn't
// leave the visitor without a chatbot for the whole session.
const VOICEFLOW_MAX_LOAD_ATTEMPTS = 4
const VOICEFLOW_RETRY_BASE_DELAY = 1500 // grows: 1.5s, 3s, 6s (exponential backoff)
// If voiceflow.chat.load() never settles (slow/unreachable runtime), retry it.
const VOICEFLOW_CHAT_LOAD_TIMEOUT = 15000
// window.voiceflow may not exist the moment the bundle's load event fires.
const VOICEFLOW_API_POLL_INTERVAL = 100
const VOICEFLOW_API_MAX_POLLS = 50 // 5s total
let voiceflowScriptQueued = false
let voiceflowScriptLoaded = false
let voiceflowLoadAttempts = 0
let hasAttitudeScrollCompleted = false
let hasPreloadedVoiceflowScript = false

// --- Storage safety ------------------------------------------------------
// Safari (and any browser set to "block all cookies", plus strict private
// modes) THROWS a SecurityError on every localStorage/sessionStorage access
// instead of returning null. Two consequences we have to handle:
//
//  1. Our own unguarded calls aborted whatever function they sat in — that is
//     what killed the chatbot bootstrap and the cookie-consent flow.
//  2. The third-party Voiceflow bundle reads localStorage unconditionally, so
//     it throws during chat.load() no matter which `persistence` we request.
//
// (1) is solved by routing every access through the helpers below. (2) can only
// be solved by giving the page a working Storage object, so when the real one
// is unusable we install a memory-backed shim. It keeps nothing across page
// loads, which is exactly what the visitor's browser setting asks for.
const createMemoryStorage = () => {
  const data = new Map()
  return {
    getItem: key => (data.has(String(key)) ? data.get(String(key)) : null),
    setItem: (key, value) => {
      data.set(String(key), String(value))
    },
    removeItem: key => {
      data.delete(String(key))
    },
    clear: () => data.clear(),
    key: index => {
      const keys = Array.from(data.keys())
      return index >= 0 && index < keys.length ? keys[index] : null
    },
    get length() {
      return data.size
    },
    __stylecastMemoryShim: true,
  }
}

// Real (browser-provided) storage per kind, or null when it throws.
const realStorage = {}
// What we actually read/write through: real storage, or the installed shim.
const activeStorage = {}

const probeStorage = kind => {
  if (typeof window === "undefined") return null
  if (kind in realStorage) return realStorage[kind]

  try {
    const store = kind === "session" ? window.sessionStorage : window.localStorage
    // Merely reading window.localStorage does not throw in Safari — only using
    // it does — so the probe has to perform a real write.
    const probe = "__stylecast_probe__"
    store.setItem(probe, "1")
    store.removeItem(probe)
    realStorage[kind] = store
  } catch (error) {
    console.warn(
      `[gatsby-browser] ${kind}Storage is blocked by the browser — using an in-memory fallback`
    )
    realStorage[kind] = null
  }

  return realStorage[kind]
}

// Replace blocked Storage objects with a working in-memory stand-in so neither
// our code nor third-party widgets explode on access. Runs once, as early as
// possible — before the Voiceflow bundle is ever injected.
const installStorageFallback = () => {
  if (typeof window === "undefined") return

  ;["local", "session"].forEach(kind => {
    if (activeStorage[kind]) return

    const real = probeStorage(kind)
    if (real) {
      activeStorage[kind] = real
      return
    }

    const shim = createMemoryStorage()
    activeStorage[kind] = shim
    try {
      Object.defineProperty(window, `${kind}Storage`, {
        value: shim,
        configurable: true,
        writable: true,
      })
    } catch (error) {
      // Cannot redefine it (rare) — our own helpers still use the shim.
      console.warn(
        `[gatsby-browser] Could not install ${kind}Storage fallback:`,
        error
      )
    }
  })
}

if (typeof window !== "undefined") {
  installStorageFallback()
}

const safeStorage = kind => {
  if (typeof window === "undefined") return null
  if (!activeStorage[kind]) installStorageFallback()
  return activeStorage[kind] || null
}

const storageGet = (key, kind = "local") => {
  const store = safeStorage(kind)
  if (!store) return null
  try {
    return store.getItem(key)
  } catch (error) {
    return null
  }
}

const storageSet = (key, value, kind = "local") => {
  const store = safeStorage(kind)
  if (!store) return
  try {
    store.setItem(key, value)
  } catch (error) {
    // Quota exceeded or storage revoked mid-session — non-fatal.
  }
}

const storageRemove = (key, kind = "local") => {
  const store = safeStorage(kind)
  if (!store) return
  try {
    store.removeItem(key)
  } catch (error) {
    // no-op
  }
}

// True only for genuine browser storage — the shim persists nothing, so the
// chat widget should not be told its history will survive a reload.
const isLocalStorageUsable = () => Boolean(probeStorage("local"))

// Consent mirrored in memory: when storage is blocked (Safari strict mode) the
// cookieConsentChanged event is the only channel that carries the user's answer.
let inMemoryConsent = null

const getStoredConsent = () => {
  if (typeof window === "undefined") return null
  try {
    const raw = storageGet("stylecast-cookie-consent")
    if (raw) return JSON.parse(raw)
  } catch (error) {
    console.warn("[gatsby-browser] Unable to read consent:", error)
  }
  return inMemoryConsent
}

// The chatbot is classified as technically necessary (client decision,
// 2026-07-28), so it loads regardless of the cookie answer — visitors who
// choose "Nur notwendige" get the assistant too. Flip this back to true to
// make the chatbot consent-gated again; nothing else needs to change.
const CHATBOT_REQUIRES_CONSENT = false

const hasVoiceflowConsent = consent =>
  !CHATBOT_REQUIRES_CONSENT ||
  Boolean(consent?.functional || consent?.marketing || consent?.analytics)

// Loading the widget is one thing, writing chat history to localStorage is
// another: that part stays opt-in. Without a decision — or after "Nur
// notwendige" — the chat runs with in-memory history only.
const hasChatHistoryConsent = consent =>
  Boolean(consent?.functional || consent?.marketing || consent?.analytics)

// Routes that should NOT show the chatbot launcher or popup.
// The product subpage stays chatbot-free; only the main site shows it.
const isChatbotDisabledRoute = () => {
  if (typeof window === "undefined") return false
  return window.location.pathname.startsWith("/product-page")
}

const queueVoiceflowLoad = () => {
  if (voiceflowScriptQueued || voiceflowScriptLoaded) return

  const consent = getStoredConsent()
  if (!hasVoiceflowConsent(consent)) {
    console.warn("Chatbot load blocked: consent not granted")
    return
  }

  voiceflowScriptQueued = true
  if (CHATBOT_LOAD_DELAY > 0) {
    setTimeout(() => {
      loadVoiceflowScript()
    }, CHATBOT_LOAD_DELAY)
  } else {
    loadVoiceflowScript()
  }
}

// Preload the Voiceflow bundle so the actual load after consent is faster.
// This only downloads the script; execution still happens later via loadVoiceflowScript().
const preloadVoiceflowScript = () => {
  if (typeof document === "undefined") return
  if (hasPreloadedVoiceflowScript) return
  hasPreloadedVoiceflowScript = true

  // Avoid duplicate link elements
  if (document.querySelector('link[data-voiceflow-preload="true"]')) return

  const link = document.createElement("link")
  link.rel = "preload"
  link.as = "script"
  link.href = "https://cdn.voiceflow.com/widget-next/bundle.mjs"
  link.setAttribute("data-voiceflow-preload", "true")
  document.head.appendChild(link)
}

// Function to add chatbot popup
const addChatbotPopup = function () {
  try {
    // Skip popup entirely on chatbot-disabled routes (e.g. product-page).
    if (isChatbotDisabledRoute()) {
      return
    }

    // Check if popup was already dismissed in this session
    if (storageGet("castbot-popup-dismissed", "session")) {
      return
    }

    // Add chatbot popup if it doesn't exist
    if (!document.getElementById("castbot-popup")) {
      console.log("Creating chatbot popup...")
      const popup = document.createElement("div")
      popup.id = "castbot-popup"
      popup.innerHTML = `
        <div class="castbot-popup" style="
          position: fixed;
          bottom: 110px;
          left: 24px;
          background: white;
          color: #1f2937;
          padding: 14px 40px 14px 16px;
          border-radius: 12px;
          font-size: 13px;
          font-weight: 500;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.08);
          z-index: 100;
          animation: slideInLeft 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
          max-width: 260px;
          line-height: 1.6;
          border: 1px solid rgba(0, 0, 0, 0.06);
        ">
          <div style="
            display: flex;
            align-items: flex-start;
          ">
            <span style="color: #374151;">${
              (storageGet("status-language") === "en")
                ? "Questions? I'm happy to help." 
                : "Fragen? Ich helfe dir gern."
            }</span>
          </div>
          <button 
            class="castbot-popup-close" 
            onclick="window.closeCastbotPopup()"
            style="
              position: absolute;
              top: 8px;
              right: 8px;
              background: transparent;
              border: none;
              cursor: pointer;
              padding: 4px;
              display: flex;
              align-items: center;
              justify-content: center;
              border-radius: 50%;
              transition: all 0.2s ease;
              width: 24px;
              height: 24px;
            "
            aria-label="Close popup"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: block;">
              <path d="M12 4L4 12M4 4L12 12" stroke="#6b7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        <style>
          @keyframes slideInLeft {
            0% {
              opacity: 0;
              transform: translateX(-20px) scale(0.95);
            }
            100% {
              opacity: 1;
              transform: translateX(0) scale(1);
            }
          }
          .castbot-popup-close:hover {
            background: rgba(0, 0, 0, 0.05) !important;
          }
          .castbot-popup-close:active {
            transform: scale(0.9);
          }
          @media (max-width: 768px) {
            .castbot-popup {
              bottom: 82px !important;
              left: 16px !important;
              max-width: 220px !important;
              font-size: 13px !important;
              padding: 14px 40px 14px 16px !important;
            }
          }
        </style>
      `
      if (document.body) {
        document.body.appendChild(popup)
        console.log("Castbot popup added successfully")

        // Auto-close after 7 seconds
        const autoCloseTimeout = setTimeout(() => {
          if (typeof window.closeCastbotPopup === "function") {
            window.closeCastbotPopup()
          }
        }, 7000)

        // Auto-close on scroll
        // Wait 500ms before attaching scroll listener to avoid catching the end of the intro animation
        setTimeout(() => {
          const scroller = document.getElementById("root") || window
          const handleScroll = () => {
            if (typeof window.closeCastbotPopup === "function") {
              window.closeCastbotPopup()
            }
            scroller.removeEventListener("scroll", handleScroll)
          }
          scroller.addEventListener("scroll", handleScroll, { passive: true })
        }, 500)
      } else {
        console.error("document.body not found")
      }
    }
  } catch (error) {
    console.error("Error adding chatbot popup:", error)
  }
}

// Function to close popup
window.closeCastbotPopup = function () {
  const popup = document.getElementById("castbot-popup")
  if (popup && !popup.dataset.closing) {
    popup.dataset.closing = "true"
    popup.style.animation = "slideOutLeft 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards"
    setTimeout(() => {
      popup.remove()
      console.log("Castbot popup removed")
    }, 600)
    storageSet("castbot-popup-dismissed", "true", "session")
  }
}

// Add the slideOut animation and chatbot z-index styles
if (typeof document !== "undefined") {
  const style = document.createElement("style")
  style.textContent = `
    @keyframes slideOutLeft {
      0% {
        opacity: 1;
        transform: translateX(0) scale(1) translateY(0);
      }
      100% {
        opacity: 0;
        transform: translateX(-30px) scale(0.9) translateY(10px);
      }
    }
    
    /* Lower z-index for Voiceflow chatbot to appear behind cookie banner */
    .vfrc-launcher,
    #voiceflow-chat,
    #voiceflow-widget,
    [class*="voiceflow"],
    [id*="voiceflow"],
    [class*="vfrc"],
    [id*="vfrc"],
    div[style*="z-index: 10000"],
    div[style*="z-index: 9999"],
    iframe[title*="voiceflow"],
    .vfrc-widget,
    .vfrc-chat,
    button[class*="vfrc"],
    div[class*="vfrc"] {
      z-index: 1 !important;
      max-z-index: 1 !important;
    }
    
    /* Force chatbot launcher specifically to low z-index */
    .vfrc-launcher,
    button.vfrc-launcher {
      z-index: 1 !important;
      position: fixed !important;
    }

    /* While the "Forged by nature" intro loader is active, the chatbot must
       never be visible over it (defensive: it is also gated from loading). */
    html.loader-active .vfrc-launcher,
    html.loader-active #voiceflow-chat,
    html.loader-active #voiceflow-widget,
    html.loader-active [class*="voiceflow"],
    html.loader-active [id*="voiceflow"],
    html.loader-active [class*="vfrc"],
    html.loader-active [id*="vfrc"] {
      opacity: 0 !important;
      visibility: hidden !important;
      pointer-events: none !important;
    }

    /* Ensure cookie banners stay on top */
    [class*="cookie"],
    [id*="cookie"],
    [class*="consent"],
    [id*="consent"],
    [class*="Cookie"],
    [id*="Cookie"],
    .CookieConsent,
    #CookiebotWidget,
    #cookiescript_injected,
    .cookie-banner,
    .cookie-consent,
    #cookie-banner,
    #cookie-consent {
      z-index: 999999 !important;
      position: relative !important;
    }
  `
  if (document.head) {
    document.head.appendChild(style)
  }
}

// Expose to window for external calls
window.addChatbotPopup = addChatbotPopup

// Function to force show popup (clears dismissed flag first)
window.showCastbotPopup = function () {
  storageRemove("castbot-popup-dismissed", "session")
  addChatbotPopup()
}

// Function to enforce z-index on all chatbot elements
window.enforceChatbotZIndex = function () {
  try {
    // Find all potential Voiceflow elements
    const voiceflowSelectors = [
      ".vfrc-launcher",
      "#voiceflow-chat",
      "#voiceflow-widget",
      '[class*="voiceflow"]',
      '[id*="voiceflow"]',
      '[class*="vfrc"]',
      '[id*="vfrc"]',
      ".vfrc-widget",
      ".vfrc-chat",
      'iframe[title*="voiceflow"]',
      'button[class*="vfrc"]',
      'div[class*="vfrc"]',
    ]

    voiceflowSelectors.forEach(selector => {
      try {
        const elements = document.querySelectorAll(selector)
        elements.forEach(el => {
          // Force z-index to 1 regardless of current value
          el.style.setProperty("z-index", "1", "important")

          // Also check parent elements
          let parent = el.parentElement
          let depth = 0
          while (parent && parent !== document.body && depth < 5) {
            const parentZIndex = window.getComputedStyle(parent).zIndex
            if (parentZIndex && parentZIndex !== "auto") {
              parent.style.setProperty("z-index", "1", "important")
            }
            parent = parent.parentElement
            depth++
          }
        })
      } catch (e) {
        // Skip if selector fails
      }
    })

    // Specifically target the launcher
    const launcher = document.querySelector(".vfrc-launcher")
    if (launcher) {
      launcher.style.setProperty("z-index", "1", "important")
      if (launcher.parentElement) {
        launcher.parentElement.style.setProperty("z-index", "1", "important")
      }
    }
  } catch (error) {
    console.error("Error enforcing chatbot z-index:", error)
  }
}

// The launcher lives in the widget's shadow DOM (every light-DOM
// ".vfrc-launcher" lookup in this file therefore finds nothing) and fades
// itself in with a CSS keyframe animation. When the container is display:none
// while that animation would run — which is exactly what happens when the
// cookie banner is still on screen as the widget boots — the animation never
// plays and the launcher stays stuck on its first keyframe: opacity 0, shifted
// down 10px. Restoring the container alone does not replay it, so the visitor
// is left with an invisible chatbot. Settle the animation by hand instead.
const ensureLauncherVisible = () => {
  const container = document.getElementById("voiceflow-chat")
  const launcher = container?.shadowRoot?.querySelector(".vfrc-launcher")
  if (!launcher) return
  if (parseFloat(window.getComputedStyle(launcher).opacity) > 0.5) return

  launcher.style.setProperty("animation", "none", "important")
  launcher.style.setProperty("opacity", "1", "important")
  launcher.style.setProperty("visibility", "visible", "important")
  launcher.style.setProperty("transform", "none", "important")
}

// Function to hide/show chatbot when the Tailwind cookie banner is visible
// Cookie banner root matches the Tailwind classes from the provided outerHTML
window.updateChatbotForCookieBanner = function () {
  try {
    const container = document.getElementById("voiceflow-chat")
    if (!container) return

    // This selector matches: <div class="fixed bottom-0 left-0 right-0 ... z-[9999]">
    const cookieBanner = document.querySelector(
      "div.fixed.bottom-0.left-0.right-0.z-\\[9999\\]"
    )

    let bannerVisible = false
    if (cookieBanner) {
      const style = window.getComputedStyle(cookieBanner)
      bannerVisible =
        style.display !== "none" &&
        style.visibility !== "hidden" &&
        cookieBanner.offsetHeight > 0 &&
        cookieBanner.offsetWidth > 0
    }

    if (bannerVisible) {
      // Completely hide chatbot while cookie banner is shown
      container.style.display = "none"
    } else {
      // Restore container; actual visibility is still controlled by delay logic
      container.style.display = ""
      // ...except when the launcher's own fade-in was swallowed while the
      // banner covered it. Not on routes that deliberately keep it hidden.
      if (!isChatbotDisabledRoute()) {
        ensureLauncherVisible()
      }
    }
  } catch (error) {
    console.error("Error updating chatbot for cookie banner:", error)
  }
}

// Run z-index + cookie checks periodically to catch dynamic changes.
// 1000ms (was 500ms) halves the steady-state main-thread work without any
// user-visible latency, since the MutationObserver already covers real changes.
if (typeof window !== "undefined") {
  setInterval(() => {
    if (window.enforceChatbotZIndex) {
      window.enforceChatbotZIndex()
    }
    if (window.updateChatbotForCookieBanner) {
      window.updateChatbotForCookieBanner()
    }
  }, 1000)
}

// Global function to resize chatbot
window.resizeChatbot = function () {
  try {
    // Check if we're in mobile mode
    const isMobile = window.innerWidth <= 768
    const launcherSize = isMobile ? "60px" : "85px"
    const imgSize = isMobile ? "42px" : "60px"
    const bottomPosition = isMobile ? "16px" : "24px"
    const rightPosition = isMobile ? "16px" : "24px"

    // Try direct querySelector
    let launcher = document.querySelector(".vfrc-launcher")

    if (launcher) {
      launcher.style.setProperty("height", launcherSize, "important")
      launcher.style.setProperty("width", launcherSize, "important")
      launcher.style.setProperty("min-width", launcherSize, "important")
      launcher.style.setProperty("max-width", launcherSize, "important")
      launcher.style.setProperty("min-height", launcherSize, "important")
      launcher.style.setProperty("max-height", launcherSize, "important")
      launcher.style.setProperty("bottom", bottomPosition, "important")
      launcher.style.setProperty("right", rightPosition, "important")
      launcher.style.setProperty("z-index", "1", "important")

      // Find and resize the img inside the launcher
      const img = launcher.querySelector("img")
      if (img) {
        img.style.setProperty("height", imgSize, "important")
        img.style.setProperty("width", imgSize, "important")
        img.style.setProperty("max-width", imgSize, "important")
        img.style.setProperty("max-height", imgSize, "important")
      }

      // Also set z-index on parent containers
      let parent = launcher.parentElement
      while (parent && parent !== document.body) {
        const parentZIndex = window.getComputedStyle(parent).zIndex
        if (parentZIndex && parentZIndex !== "auto") {
          parent.style.setProperty("z-index", "1", "important")
        }
        parent = parent.parentElement
      }

      return
    }

    // Check for iframes
    const iframes = document.querySelectorAll("iframe")

    iframes.forEach((iframe, index) => {
      try {
        const iframeDoc =
          iframe.contentDocument || iframe.contentWindow.document
        if (iframeDoc) {
          const launcherInIframe = iframeDoc.querySelector(".vfrc-launcher")
          if (launcherInIframe) {
            launcherInIframe.style.setProperty(
              "height",
              launcherSize,
              "important"
            )
            launcherInIframe.style.setProperty(
              "width",
              launcherSize,
              "important"
            )
            launcherInIframe.style.setProperty(
              "min-width",
              launcherSize,
              "important"
            )
            launcherInIframe.style.setProperty(
              "max-width",
              launcherSize,
              "important"
            )

            // Find and resize the img inside the launcher in iframe
            const imgInIframe = launcherInIframe.querySelector("img")
            if (imgInIframe) {
              imgInIframe.style.setProperty("height", imgSize, "important")
              imgInIframe.style.setProperty("width", imgSize, "important")
            }
            return
          }
        }
      } catch (e) {
        // Cross-origin iframe, skip
      }
    })

    // Check for shadow DOM
    const shadowHosts = document.querySelectorAll("*")
    for (let host of shadowHosts) {
      if (host.shadowRoot) {
        const launcherInShadow = host.shadowRoot.querySelector(".vfrc-launcher")
        if (launcherInShadow) {
          launcherInShadow.style.setProperty(
            "height",
            launcherSize,
            "important"
          )
          launcherInShadow.style.setProperty("width", launcherSize, "important")
          launcherInShadow.style.setProperty(
            "min-width",
            launcherSize,
            "important"
          )
          launcherInShadow.style.setProperty(
            "max-width",
            launcherSize,
            "important"
          )

          // Find and resize the img inside the launcher in shadow DOM
          const imgInShadow = launcherInShadow.querySelector("img")
          if (imgInShadow) {
            imgInShadow.style.setProperty("height", imgSize, "important")
            imgInShadow.style.setProperty("width", imgSize, "important")
          }
          return
        }
      }
    }

    // Enforce z-index after resizing
    if (window.enforceChatbotZIndex) {
      window.enforceChatbotZIndex()
    }
    if (window.updateChatbotForCookieBanner) {
      window.updateChatbotForCookieBanner()
    }
  } catch (error) {
    console.error("Error resizing chatbot:", error)
  }
}

// The chatbot launcher hides in the same zones as the sample-order button:
// on the landing page (Attitude section still below the viewport) and over the
// giant "stylecast" footer wordmark. It stays visible across the content
// sections in between.
const shouldHideChatbotForScroll = () => {
  if (typeof window === "undefined") return false
  const attitude = document.getElementById("attitude")
  if (attitude && attitude.getBoundingClientRect().top > window.innerHeight * 0.5) {
    return true
  }
  const mark = document.querySelector(".stylecast-letters")
  if (mark && mark.getBoundingClientRect().top < window.innerHeight - 40) {
    return true
  }
  return false
}

// The Voiceflow launcher lives inside a shadow root / iframe, so a plain
// document query misses it. Reach it wherever it renders and set visibility.
// The desired state is remembered so DOM mutations can re-assert it.
let chatbotHiddenState = false
const applyLauncherVisibility = hide => {
  if (typeof document === "undefined") return
  chatbotHiddenState = hide
  const apply = el => {
    if (!el) return
    el.style.setProperty(
      "transition",
      "opacity 0.6s ease, visibility 0.6s ease",
      "important"
    )
    el.style.setProperty("opacity", hide ? "0" : "1", "important")
    el.style.setProperty("visibility", hide ? "hidden" : "visible", "important")
    el.style.setProperty("pointer-events", hide ? "none" : "auto", "important")
  }
  // Light DOM
  document.querySelectorAll(".vfrc-launcher").forEach(apply)
  // Inside same-origin iframes
  document.querySelectorAll("iframe").forEach(iframe => {
    try {
      const doc = iframe.contentDocument || iframe.contentWindow.document
      if (doc) doc.querySelectorAll(".vfrc-launcher").forEach(apply)
    } catch (e) {
      // cross-origin iframe — skip
    }
  })
  // Inside shadow roots
  document.querySelectorAll("*").forEach(host => {
    if (host.shadowRoot) {
      host.shadowRoot.querySelectorAll(".vfrc-launcher").forEach(apply)
    }
  })
}

// Re-assert the remembered visibility (used after Voiceflow re-renders).
const reassertLauncherVisibility = () => applyLauncherVisibility(chatbotHiddenState)

// Locate the launcher wherever Voiceflow renders it (light DOM, iframe, or
// shadow root). Returns the first match or null.
const findAnyLauncher = () => {
  if (typeof document === "undefined") return null
  const direct = document.querySelector(".vfrc-launcher")
  if (direct) return direct
  const iframes = document.querySelectorAll("iframe")
  for (const iframe of iframes) {
    try {
      const doc = iframe.contentDocument || iframe.contentWindow.document
      const found = doc && doc.querySelector(".vfrc-launcher")
      if (found) return found
    } catch (e) {
      // cross-origin iframe — skip
    }
  }
  const all = document.querySelectorAll("*")
  for (const host of all) {
    if (host.shadowRoot) {
      const found = host.shadowRoot.querySelector(".vfrc-launcher")
      if (found) return found
    }
  }
  return null
}

const loadVoiceflowScript = () => {
  if (typeof window === "undefined") return
  if (voiceflowScriptLoaded) return
  if (window.__voiceflow_loaded) return

  const consent = getStoredConsent()
  if (!hasVoiceflowConsent(consent)) {
    console.warn("Skipping chatbot load - consent not granted")
    voiceflowScriptQueued = false
    return
  }

  voiceflowScriptLoaded = true
  window.__voiceflow_loaded = true
  voiceflowLoadAttempts += 1

  console.log(`Loading chatbot script... (attempt ${voiceflowLoadAttempts})`)

  const v = document.createElement("script")
  const s = document.getElementsByTagName("script")[0]

  const runVoiceflowInit = function () {
    // Only load chat if not already loaded
    if (!window.__voiceflow_chat_initialized) {
      window.__voiceflow_chat_initialized = true

      // Use responsive button size based on screen width
      const isMobile = window.innerWidth <= 768
      const buttonSize = isMobile ? "60px" : "85px"

      // Initialize the chat widget. If load() never settles (slow/unreachable
      // runtime) or rejects, retry a couple of times before giving up so a
      // transient runtime issue doesn't leave the launcher missing.
      // Flipped to true once a load attempt dies on a storage error, so the
      // retry drops persistence instead of repeating the same failure.
      let forceMemoryPersistence = false

      const isStorageError = error => {
        const name = error && error.name ? String(error.name) : ""
        const message = error && error.message ? String(error.message) : ""
        return (
          name === "SecurityError" ||
          name === "QuotaExceededError" ||
          /storage|quota|insecure|denied/i.test(message)
        )
      }

      const initVoiceflowChat = (attempt = 1) => {
        let settled = false
        const timeoutId = setTimeout(() => {
          if (settled) return
          if (attempt < 3) {
            console.warn(
              `Voiceflow chat.load() timed out (attempt ${attempt}) - retrying`
            )
            initVoiceflowChat(attempt + 1)
          } else {
            console.error("Voiceflow chat.load() did not initialize in time")
          }
        }, VOICEFLOW_CHAT_LOAD_TIMEOUT)

        // Safari blocks localStorage entirely under "block all cookies" and in
        // some private-browsing contexts; asking Voiceflow to persist there
        // makes its own load() throw. Degrade to in-memory history instead.
        // Same downgrade without an optional-cookie opt-in: the widget itself
        // is necessary, storing the conversation across visits is not.
        const persistence =
          !forceMemoryPersistence &&
          isLocalStorageUsable() &&
          hasChatHistoryConsent(getStoredConsent())
            ? "localStorage"
            : "memory"

        // load() must never throw synchronously — a TypeError here would abort
        // the rest of this handler (popup, sizing, observers) for good.
        let loadPromise
        try {
          loadPromise = window.voiceflow.chat.load({
            verify: { projectID: "68c01d702cdcdc32a52b2db1" },
            url: "https://general-runtime.voiceflow.com",
            versionID: "production",
            assistant: {
              buttonWidth: buttonSize,
              buttonHeight: buttonSize,
              // Persist history when the browser actually allows storage
              persistence,
            },
          })
        } catch (error) {
          loadPromise = Promise.reject(error)
        }

        Promise.resolve(loadPromise)
          .then(() => {
            settled = true
            clearTimeout(timeoutId)
            // Immediately close the chat once after load to avoid auto-open
            if (
              window.voiceflow &&
              window.voiceflow.chat &&
              typeof window.voiceflow.chat.close === "function"
            ) {
              window.voiceflow.chat.close()
            }
          })
          .catch(error => {
            settled = true
            clearTimeout(timeoutId)

            // Voiceflow's own persistence layer throws when the browser denies
            // storage access. Retrying identically would fail identically, so
            // drop to in-memory history: a chatbot without saved history beats
            // no chatbot at all.
            if (isStorageError(error) && !forceMemoryPersistence) {
              console.warn(
                "Voiceflow chat.load() hit blocked storage - retrying without persistence",
                error
              )
              forceMemoryPersistence = true
              setTimeout(() => initVoiceflowChat(attempt), 300)
              return
            }

            if (attempt < 3) {
              console.warn(
                `Voiceflow chat.load() failed (attempt ${attempt}) - retrying`,
                error
              )
              setTimeout(() => initVoiceflowChat(attempt + 1), 1500)
            } else {
              console.error("Voiceflow chat.load() failed permanently", error)
            }
          })
      }

      initVoiceflowChat()

      // Keep the launcher invisible from the instant it renders, then reveal it
      // in one coordinated step together with the "Muster bestellen" button.
      // The chatbot's entrance is adapted to the button: same 0.7s ease-in, and
      // it fires "chatbotLauncherReady" at the exact moment it appears so both
      // elements come in simultaneously.
      let launcherRevealed = false

      // Suppress any flash before we deliberately reveal it.
      const preHideLauncher = () => {
        if (findAnyLauncher() && !launcherRevealed) {
          applyLauncherVisibility(true)
        }
      }

      const revealLauncher = () => {
        const launcher = findAnyLauncher()
        if (!launcher || launcherRevealed) return !!launcher
        launcherRevealed = true

        applyLauncherVisibility(shouldHideChatbotForScroll())
        launcher.style.setProperty("z-index", "1", "important")
        if (window.enforceChatbotZIndex) {
          window.enforceChatbotZIndex()
        }

        // Shared entrance signal — the sample-order button reveals on this.
        window.dispatchEvent(new CustomEvent("chatbotLauncherReady"))

        // Clear the dismissed flag and add chatbot popup shortly after the
        // chatbot is visible. storageRemove() instead of sessionStorage:
        // Safari with blocked storage throws on direct access.
        storageRemove("castbot-popup-dismissed", "session")
        setTimeout(() => addChatbotPopup(), 300)
        return true
      }

      // Poll until the launcher exists, hiding it meanwhile, then reveal once.
      const revealPoll = setInterval(() => {
        preHideLauncher()
        if (revealLauncher()) clearInterval(revealPoll)
      }, 40)
      setTimeout(() => clearInterval(revealPoll), 10000)

      // Try to resize with aggressive timing
      setTimeout(() => window.resizeChatbot(), 500)
      setTimeout(() => window.resizeChatbot(), 1000)
      setTimeout(() => window.resizeChatbot(), 1500)
      setTimeout(() => window.resizeChatbot(), 2000)
      setTimeout(() => window.resizeChatbot(), 3000)
      setTimeout(() => window.resizeChatbot(), 5000)

      // Also set up a MutationObserver to catch dynamic changes.
      // Debounced so the (expensive) resize/z-index scans run at most every
      // ~250ms instead of on every single DOM mutation, and re-targeted to the
      // Voiceflow container once it exists so app-wide mutations don't trigger it.
      let mutationDebounce = null
      const runChatbotMaintenance = () => {
        window.resizeChatbot()
        if (window.enforceChatbotZIndex) {
          window.enforceChatbotZIndex()
        }
        if (window.updateChatbotForCookieBanner) {
          window.updateChatbotForCookieBanner()
        }
        // Re-apply the landing/footer hide if Voiceflow re-rendered the launcher.
        reassertLauncherVisibility()
      }
      const observer = new MutationObserver(() => {
        if (mutationDebounce) return
        mutationDebounce = setTimeout(() => {
          mutationDebounce = null
          runChatbotMaintenance()

          // Once the widget container exists, narrow the observer to it so we
          // stop reacting to unrelated changes in the rest of the app.
          const container = document.getElementById("voiceflow-chat")
          if (container && observer.__target !== container) {
            observer.disconnect()
            observer.__target = container
            observer.observe(container, { childList: true, subtree: true })
          }
        }, 250)
      })
      observer.observe(document.body, { childList: true, subtree: true })

      // Enforce z-index immediately after load
      setTimeout(() => {
        if (window.enforceChatbotZIndex) {
          window.enforceChatbotZIndex()
        }
      }, 100)
      setTimeout(() => {
        if (window.enforceChatbotZIndex) {
          window.enforceChatbotZIndex()
        }
      }, 500)
      setTimeout(() => {
        if (window.enforceChatbotZIndex) {
          window.enforceChatbotZIndex()
        }
      }, 1000)

      // Add window resize listener to handle responsive sizing
      let resizeTimeout
      window.addEventListener("resize", () => {
        clearTimeout(resizeTimeout)
        resizeTimeout = setTimeout(() => {
          window.resizeChatbot()
        }, 250)
      })
    }
  }

  // The CDN bundle does not always have window.voiceflow ready the instant the
  // script's load event fires (Safari in particular defers execution of the
  // .mjs bundle in some cases). Poll briefly instead of dereferencing blindly —
  // a TypeError here used to leave the launcher missing with no retry.
  const waitForVoiceflowApi = (attempt = 0) => {
    if (window.voiceflow && window.voiceflow.chat) {
      try {
        runVoiceflowInit()
      } catch (error) {
        console.error("Voiceflow init failed", error)
        window.__voiceflow_chat_initialized = false
      }
      return
    }

    if (attempt >= VOICEFLOW_API_MAX_POLLS) {
      console.error(
        "Voiceflow bundle loaded but window.voiceflow.chat never appeared"
      )
      return
    }

    setTimeout(() => waitForVoiceflowApi(attempt + 1), VOICEFLOW_API_POLL_INTERVAL)
  }

  v.onload = function () {
    waitForVoiceflowApi()
  }

  v.onerror = function () {
    // Reset load flags so a retry can run.
    voiceflowScriptLoaded = false
    voiceflowScriptQueued = false
    window.__voiceflow_loaded = false

    // Remove the failed script node so the retry inserts a clean one.
    if (v.parentNode) {
      v.parentNode.removeChild(v)
    }

    if (voiceflowLoadAttempts < VOICEFLOW_MAX_LOAD_ATTEMPTS) {
      const delay =
        VOICEFLOW_RETRY_BASE_DELAY * Math.pow(2, voiceflowLoadAttempts - 1)
      console.warn(
        `Failed to load Voiceflow script (attempt ${voiceflowLoadAttempts}/${VOICEFLOW_MAX_LOAD_ATTEMPTS}) - retrying in ${delay}ms`
      )
      setTimeout(() => loadVoiceflowScript(), delay)
    } else {
      console.error(
        `Failed to load Voiceflow script after ${VOICEFLOW_MAX_LOAD_ATTEMPTS} attempts`
      )
    }
  }

  v.src = "https://cdn.voiceflow.com/widget-next/bundle.mjs"
  v.type = "text/javascript"
  // insertBefore(v, s) throws when the page has no script tag / no parentNode
  // (possible on Safari bfcache restores) — fall back to a plain head append.
  if (s && s.parentNode) {
    s.parentNode.insertBefore(v, s)
  } else {
    ;(document.head || document.documentElement).appendChild(v)
  }
}

export const onInitialClientRender = () => {
  if (typeof window === "undefined") return

  console.log("onInitialClientRender called")

  // Clear the popup dismissed flag so it shows on every page load
  storageRemove("castbot-popup-dismissed", "session")

  // Flipped once the grace period for an unanswered cookie banner is over.
  let consentAnswerWaitElapsed = false

  // Respect the intro animation: only queue the chatbot load after the
  // automatic scroll to the second section has completed. (Consent no longer
  // gates the load itself — see CHATBOT_REQUIRES_CONSENT.)
  const maybeQueueVoiceflow = () => {
    // Never load chatbot on disabled routes (e.g. product-page).
    if (isChatbotDisabledRoute()) {
      return
    }

    const consent = getStoredConsent()

    // Never load while the "Forged by nature" intro loader is on screen.
    if (document.documentElement.classList.contains("loader-active")) {
      return
    }

    // Wait until the intro scroll animation has finished
    if (!hasAttitudeScrollCompleted) {
      return
    }

    // Give an unanswered cookie banner a moment (see
    // CHATBOT_CONSENT_ANSWER_WAIT) so the load picks the right persistence mode.
    if (!consent && !consentAnswerWaitElapsed) {
      return
    }

    if (hasVoiceflowConsent(consent)) {
      preloadVoiceflowScript()
      queueVoiceflowLoad()
    }
  }

  // Hide the chatbot launcher (and its popup) on the landing page and over the
  // "stylecast" footer wordmark — the same zones the sample-order button uses —
  // and keep it visible across the content sections in between.
  let chatbotScrollHideAttached = false
  const attachChatbotScrollHide = () => {
    if (chatbotScrollHideAttached) return
    const scroller = document.getElementById("root") || window
    if (!scroller) return
    chatbotScrollHideAttached = true

    let hidden = null

    const applyScrollState = () => {
      if (isChatbotDisabledRoute()) return
      const past = shouldHideChatbotForScroll()
      if (past === hidden) return
      hidden = past

      applyLauncherVisibility(past)

      if (past && typeof window.closeCastbotPopup === "function") {
        window.closeCastbotPopup()
      }
    }

    scroller.addEventListener("scroll", applyScrollState, { passive: true })
    applyScrollState()
  }

  // Single entry point for opening the chatbot gate. Idempotent: the first
  // trigger to fire wins, every later one is a cheap no-op.
  const openChatbotGate = reason => {
    if (hasAttitudeScrollCompleted) return
    hasAttitudeScrollCompleted = true
    if (reason) {
      console.log(`Chatbot gate opened via: ${reason}`)
    }
    maybeQueueVoiceflow()
    // Sobald der Chatbot kommen darf, übernimmt das Scroll-Verstecken die
    // Sichtbarkeit — Landingpage und Footer-Schriftzug bleiben frei.
    attachChatbotScrollHide()
    cleanupGateTriggers()
  }

  // Listen for the scroll completion event dispatched from the landing page
  // animation. This is the ONLY trigger that opens the gate on a normal visit —
  // deliberately no pageshow/focus/interaction shortcuts, because every one of
  // them fires while the loader is still on screen and made the chatbot pop up
  // in the middle of the intro.
  const handleAttitudeScrollComplete = () =>
    openChatbotGate("attitudeScrollComplete")

  // The intro only plays on the landing page, and it is skipped entirely when
  // the visitor arrives on a hash deep link — nothing to wait for in either case.
  const introPlaysHere = () => {
    if (window.location.hash && window.location.hash !== "#home") return false
    return window.location.pathname.replace(/\/+$/, "") === ""
  }

  const now = () =>
    window.performance && window.performance.now
      ? window.performance.now()
      : Date.now()

  // Foreground-time fallback: never let a missing/interrupted scroll animation
  // permanently block the chatbot. Background/inactive tabs throttle
  // requestAnimationFrame to ~0fps (the intro freezes instead of completing)
  // and clamp setTimeout, so the timer only runs while the tab is visible and
  // is paused/resumed with it.
  let fallbackTimer = null
  let fallbackRemaining = CHATBOT_SCROLL_GATE_FALLBACK
  let fallbackStartedAt = 0

  const startFallbackTimer = () => {
    if (fallbackTimer !== null || hasAttitudeScrollCompleted) return
    fallbackStartedAt = now()
    fallbackTimer = setTimeout(() => {
      fallbackTimer = null
      openChatbotGate("timeout-fallback")
    }, fallbackRemaining)
  }

  const pauseFallbackTimer = () => {
    if (fallbackTimer === null) return
    clearTimeout(fallbackTimer)
    fallbackTimer = null
    fallbackRemaining = Math.max(
      0,
      fallbackRemaining - (now() - fallbackStartedAt)
    )
  }

  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      startFallbackTimer()
    } else {
      pauseFallbackTimer()
    }
  }

  function cleanupGateTriggers() {
    window.removeEventListener(
      "attitudeScrollComplete",
      handleAttitudeScrollComplete
    )
    document.removeEventListener("visibilitychange", handleVisibilityChange)
    pauseFallbackTimer()
  }

  window.addEventListener(
    "attitudeScrollComplete",
    handleAttitudeScrollComplete
  )
  document.addEventListener("visibilitychange", handleVisibilityChange)

  if (!introPlaysHere()) {
    openChatbotGate("no-intro-on-this-route")
  } else if (document.visibilityState === "visible") {
    startFallbackTimer()
  }

  // Visitors who simply ignore the banner still get the chatbot — just with
  // in-memory chat history, which is the correct default without an opt-in.
  setTimeout(() => {
    consentAnswerWaitElapsed = true
    maybeQueueVoiceflow()
  }, CHATBOT_CONSENT_ANSWER_WAIT)

  // Initial check (in case consent is already present; actual load waits for the gate)
  maybeQueueVoiceflow()

  // The event payload is the authoritative consent signal: when Safari blocks
  // storage the banner cannot persist anything, so this is the only way the
  // answer reaches us (it still decides chat-history persistence). The answer
  // deliberately does NOT open the gate: the banner is on screen during the
  // intro, and an early click would otherwise pull the chatbot in over the
  // loader. The load still waits for the intro to finish.
  const handleConsentChange = event => {
    if (event && event.detail) {
      inMemoryConsent = event.detail
    }
    maybeQueueVoiceflow()
  }
  window.addEventListener("cookieConsentChanged", handleConsentChange)
  window.addEventListener("storage", handleConsentChange)
}

// Also run on route updates
export const onRouteUpdate = ({ location, prevLocation }) => {
  // Send virtual page_view to GTM/GA4 on SPA route changes
  if (typeof window !== "undefined") {
    window.dataLayer = window.dataLayer || []
    try {
      const path =
        (location && location.pathname
          ? location.pathname
          : window.location.pathname) +
        (location && location.search
          ? location.search
          : window.location.search) +
        (location && location.hash ? location.hash : window.location.hash)
      window.dataLayer.push({
        event: "page_view",
        page_location: window.location.href,
        page_path: path,
        page_title: document.title,
      })
    } catch (e) {
      // no-op
    }
  }

  // Hide chatbot temporarily when navigating between pages
  const launcher = document.querySelector(".vfrc-launcher")
  if (launcher) {
    launcher.style.opacity = "0"
    launcher.style.pointerEvents = "none"
  }

  // On chatbot-disabled routes, also dismiss the popup and keep launcher hidden.
  if (isChatbotDisabledRoute()) {
    if (typeof window.closeCastbotPopup === "function") {
      window.closeCastbotPopup()
    }
    if (launcher) {
      launcher.style.visibility = "hidden"
    }
    return
  }

  // Show chatbot after a delay (3 seconds)
  setTimeout(() => {
    // Re-check route in case user navigated again before the timeout fired.
    if (isChatbotDisabledRoute()) return

    const launcher = document.querySelector(".vfrc-launcher")
    if (launcher) {
      launcher.style.opacity = "1"
      launcher.style.visibility = "visible"
      launcher.style.pointerEvents = "auto"
      launcher.style.transition = "opacity 0.5s ease-in-out"
      launcher.style.setProperty("z-index", "1", "important")
    }

    if (window.resizeChatbot) {
      window.resizeChatbot()
    }

    // Enforce z-index + cookie banner logic after route change
    if (window.enforceChatbotZIndex) {
      window.enforceChatbotZIndex()
    }
    if (window.updateChatbotForCookieBanner) {
      window.updateChatbotForCookieBanner()
    }
  }, 3000)
}

export const wrapRootElement = ({ element }) => {
  return <LanguageProvider>{element}</LanguageProvider>
}
